export interface Lease {
  owner: string;
  resource: string;
  epoch: number;
  until: number;
}
/** Finite slots with expiry and explicit cancellation; identity never depends on
 * an instancing index, route cursor, body handle or visible representation. */
export class Reservations {
  private leases = new Map<string, Lease[]>();
  private owners = new Map<string, Set<string>>();
  constructor(readonly epoch: () => number) {}
  acquire(resource: string, owner: string, now: number, capacity = 1, seconds = 8): boolean {
    let leases = this.leases.get(resource);
    if (!leases) this.leases.set(resource, (leases = []));
    for (let i = leases.length - 1; i >= 0; i--)
      if (leases[i].epoch !== this.epoch() || leases[i].until < now) {
        this.forget(leases[i].owner, resource);
        leases.splice(i, 1);
      }
    const prior = leases.find((l) => l.owner === owner);
    if (prior) {
      prior.until = now + seconds;
      return true;
    }
    if (leases.length >= capacity) return false;
    leases.push({ owner, resource, epoch: this.epoch(), until: now + seconds });
    let resources = this.owners.get(owner);
    if (!resources) this.owners.set(owner, (resources = new Set()));
    resources.add(resource);
    return true;
  }
  private forget(owner: string, resource: string): void {
    const resources = this.owners.get(owner);
    resources?.delete(resource);
    if (!resources?.size) this.owners.delete(owner);
  }
  release(owner: string, resource?: string): void {
    const resources = this.owners.get(owner);
    if (!resources) return;
    for (const id of resource ? [resource] : resources) {
      const leases = this.leases.get(id);
      if (!leases) continue;
      const index = leases.findIndex((l) => l.owner === owner);
      if (index >= 0) leases.splice(index, 1);
      if (leases.length) this.leases.set(id, leases);
      else this.leases.delete(id);
      if (resource) this.forget(owner, id);
    }
    if (!resource) this.owners.delete(owner);
  }
  holders(resource: string, now: number): string[] {
    return (this.leases.get(resource) ?? [])
      .filter((l) => l.epoch === this.epoch() && l.until >= now)
      .map((l) => l.owner);
  }
  clear(): void {
    this.leases.clear();
    this.owners.clear();
  }
  get size(): number {
    return [...this.leases.values()].reduce((n, l) => n + l.length, 0);
  }
}
